import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * SubscriptionContext — memuat effective subscription tenant admin yang login.
 * Sumber: GET /api/tenant/subscription/current (memerlukan tenant_admin cookie).
 * Kalau user tidak login sebagai admin, TIDAK di-fetch (skip).
 */
const SubscriptionContext = createContext({
  loading: false, subscription: null, refresh: async () => {},
});

export function SubscriptionProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const isAdmin = !!user && (user.role === "admin" || user.role === "owner");
  // Start with loading:true so AdminLayout can gate children mount before we know status.
  // Critical: while auth is still resolving, we CANNOT decide anything — keep loading:true.
  // Only once auth resolves do we either fetch (admin) or clear (non-admin).
  const [state, setState] = useState({ loading: true, subscription: null });

  const load = useCallback(async () => {
    if (authLoading) return; // auth not resolved yet — keep loading:true
    if (!isAdmin) { setState({ loading: false, subscription: null }); return; }
    setState((s) => ({ ...s, loading: true }));
    try {
      const r = await api.get("/tenant/subscription/current");
      setState({ loading: false, subscription: r.data });
    } catch {
      setState({ loading: false, subscription: null });
    }
  }, [isAdmin, authLoading]);

  useEffect(() => { load(); }, [load]);

  return (
    <SubscriptionContext.Provider value={{ ...state, refresh: load }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  return useContext(SubscriptionContext);
}
